import { CellContext } from '@tanstack/react-table';
import {
  SendMessageIcon,
  DeleteIcon,
  BenefitSetIcon,
  BudgetIcon,
  EditIcon
} from '../../../../icons';
import { ActionsMenuCell, ActionMenuItem } from '../../shared/actions-menu-cell';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { EmployeeListItem } from '@pxtra-client-lib-legacy/gen';
import {
  useDeleteEmployee,
  useDownloadEmployeeInvitationPdf,
  useSendEmployeeInvitation
} from '../../../../../queries/employee/mutations';
import { downloadBlob } from '../../../../util';
import { useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import { ConfirmationDialog } from '../../../compound';
import { getEmployeeInvitationApiErrorMessage } from '../../../../util/apiErrorMessage';
import { useEffect, useState, useMemo } from 'react';
import { useActions } from '../../../../../hooks/useActions';
import { useStore } from '../../../../../hooks/useStore';
import { useListBenefitSet } from '../../../../../queries/benefit/benefitSet';
import { usePaymentData } from '../../../../../queries/company';
import { checkHasCompanyBenefitSets } from '../../../../util/pricingModel';

export function ActionsCell({ info }: Readonly<{ info: CellContext<EmployeeListItem, unknown> }>) {
  const { t } = useTranslation();
  const { setSelectedEmployees } = useActions();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { companyStore } = useStore();

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showInvitationDialog, setShowInvitationDialog] = useState(false);
  const [showPdfInvitationDialog, setShowPdfInvitationDialog] = useState(false);
  const [menuDisabled, setDisableMenu] = useState(false);
  const [deletionDisabled, setDeletionDisabled] = useState(false);
  const [assignmentDisabled, setAssignmentDisabled] = useState(false);
  const [invitationDisabled, setInvitationDisabled] = useState(false);

  const { data: paymentData } = usePaymentData(companyStore.company?.id);
  const { data: benefitSetData } = useListBenefitSet(
    companyStore.company?.id ?? '',
    paymentData?.pricing_model
  );

  const hasBenefitSets = checkHasCompanyBenefitSets(
    paymentData?.pricing_model,
    paymentData?.booked_benefits
  );

  useEffect(() => {
    setDisableMenu(Object.keys(info.table.getState().rowSelection).length > 0);
    setDeletionDisabled(
      !['INACTIVE', 'INVITATION_PENDING_EMAIL', 'INVITATION_PENDING_PDF'].includes(
        info.row.original.status ?? ''
      )
    );
    setInvitationDisabled(hasBenefitSets && info.row.original.next_month_benefit_set == null);
    setAssignmentDisabled(benefitSetData?.results.length == 0);
  }, [
    info.table.getState().rowSelection,
    benefitSetData?.results.length,
    info.row.original.status,
    info.row.original.next_month_benefit_set,
    hasBenefitSets
  ]);

  const { mutate: sendInvitation } = useSendEmployeeInvitation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      enqueueSnackbar(
        t('employee:EMPLOYEE_INVITATION_SENT', {
          count: 1
        }),
        {
          variant: 'success'
        }
      );
    },
    onError: (error: unknown) => {
      const apiMessage = getEmployeeInvitationApiErrorMessage(error, t);
      if (apiMessage) {
        enqueueSnackbar(apiMessage, { variant: 'error' });
        return;
      }
      enqueueSnackbar(
        t('employee:ERROR_SENDING_EMPLOYEE_INVITATION', {
          error: error instanceof Error ? error.message : 'Unknown error'
        }),
        {
          variant: 'error'
        }
      );
    }
  });

  const isResigned = info.row.original.status === 'RESIGNED';

  const { mutate: downloadInvitationPdf } = useDownloadEmployeeInvitationPdf({
    onSuccess: (blob: Blob, variables: { id: string; filename?: string }) => {
      const safeName = `${info.row.original.last_name ?? 'mitarbeitende'}`
        .replaceAll(/[^A-Za-z0-9._-]+/g, '_')
        .toLowerCase();
      downloadBlob(blob, variables.filename ?? `pxtra-einladung-${safeName}.pdf`);
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      enqueueSnackbar(t('employee:INVITATION_PDF_GENERATED'), { variant: 'success' });
    },
    onError: () => {
      enqueueSnackbar(t('employee:INVITATION_PDF_ERROR'), { variant: 'error' });
    }
  });

  const { mutate: deleteEmployee } = useDeleteEmployee({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      enqueueSnackbar(t('employee:EMPLOYEE_DELETED'), {
        variant: 'success'
      });
    },
    onError: (error: unknown) => {
      enqueueSnackbar(
        t('employee:ERROR_DELETING_EMPLOYEE', {
          error: error instanceof Error ? error.message : 'Unknown error'
        }),
        {
          variant: 'error'
        }
      );
    }
  });

  const menuItems: ActionMenuItem[] = useMemo(() => {
    const editItem: ActionMenuItem = {
      color: 'primary.main',
      icon: <EditIcon />,
      label: t('common:EDIT_PROFILE'),
      onClick: () => {
        navigate(`/employees/${info.row.original.id}/data/personal/edit`);
      }
    };
    const budgetItem: ActionMenuItem = {
      color: 'primary.main',
      icon: <BudgetIcon />,
      label: t('company:EDIT_BUDGET'),
      onClick: () => {
        navigate(`/employees/${info.row.original.id}/benefits/budget`);
      }
    };
    const inviteItem: ActionMenuItem = {
      color: invitationDisabled ? 'grey.400' : 'primary.main',
      icon: <SendMessageIcon color={invitationDisabled ? 'grey.400' : 'primary.main'} />,
      label: t('company:SEND_INVITATION'),
      onClick: () =>
        info.row.original.has_work_email === true
          ? setShowInvitationDialog(true)
          : setShowPdfInvitationDialog(true),
      disabled: invitationDisabled,
      tooltipText: t('benefitSet:invitationPossibleIfBenefitSet')
    };
    const deleteItem: ActionMenuItem = {
      color: deletionDisabled ? 'grey.400' : 'error.main',
      icon: <DeleteIcon color={deletionDisabled ? 'grey.400' : 'error.main'} />,
      label: t('common:DELETE'),
      onClick: () => setShowDeleteDialog(true),
      disabled: deletionDisabled,
      hasDivider: true,
      tooltipText: t('company:DELETE_EMPLOYEE_BLOCKED')
    };
    const benefitSetItem: ActionMenuItem | null = hasBenefitSets
      ? {
          color: assignmentDisabled ? 'grey.400' : 'primary.main',
          icon: <BenefitSetIcon color={assignmentDisabled ? 'grey.400' : 'primary.main'} />,
          label: t('benefitSet:assignBenefitSet'),
          onClick: () => {
            const employeeId = info.row.original.id;
            if (employeeId) {
              setSelectedEmployees({ [employeeId]: true });
              navigate('/actions/benefit-set/assign_benefit_set/1');
            }
          },
          disabled: assignmentDisabled,
          tooltipText: t('benefitSet:assignmentPossibleIfBenefitSet')
        }
      : null;

    return [
      editItem,
      budgetItem,
      ...(benefitSetItem ? [benefitSetItem] : []),
      inviteItem,
      deleteItem
    ];
  }, [
    t,
    navigate,
    info.row.original.id,
    invitationDisabled,
    deletionDisabled,
    assignmentDisabled,
    hasBenefitSets,
    setSelectedEmployees,
    info.row.original.has_work_email
  ]);
  return (
    <>
      <ActionsMenuCell items={menuItems} disabled={menuDisabled} />
      <ConfirmationDialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onCancel={() => setShowDeleteDialog(false)}
        onConfirm={() => {
          deleteEmployee({ id: info.row.original.id ?? '' });
        }}
        title={t('confirm_dialogs:DELETE_TITLE_employee', {
          count: 1
        })}
        contentText={t('confirm_dialogs:DELETE_CONTENT_employee', {
          employee_name: `${info.row.original.first_name} ${info.row.original.last_name}`
        })}
        variant="delete"
      />
      <ConfirmationDialog
        open={showInvitationDialog}
        onClose={() => setShowInvitationDialog(false)}
        onConfirm={() => {
          sendInvitation({ id: info.row.original.id ?? '' });
        }}
        title={
          isResigned
            ? t('employee:INVITE_RESIGNED_TITLE')
            : info.row.original.available_benefit_budget_next_month === 0
              ? t('employee:INVITE_NO_BUDGET')
              : t('company:SEND_INVITATION_TITLE')
        }
        contentText={
          isResigned
            ? t('employee:INVITE_RESIGNED_WARNING')
            : info.row.original.available_benefit_budget_next_month === 0
              ? t('employee:INVITATION_WARNING_NO_BUDGET')
              : ''
        }
        variant={isResigned ? 'warning' : 'information'}
      />
      <ConfirmationDialog
        open={showPdfInvitationDialog}
        onClose={() => setShowPdfInvitationDialog(false)}
        onConfirm={() => {
          downloadInvitationPdf({ id: info.row.original.id ?? '' });
          setShowPdfInvitationDialog(false);
        }}
        title={t('employee:DOWNLOAD_INVITATION_PDF_TITLE')}
        contentText={
          info.row.original.status === 'INVITATION_PENDING_PDF'
            ? t('employee:DOWNLOAD_INVITATION_PDF_REISSUE_WARNING')
            : t('employee:INVITE_VIA_PDF_INFO')
        }
        confirmButtonText={t('employee:INVITE')}
        variant="warning"
      />
    </>
  );
}
